import { cva, type VariantProps } from 'class-variance-authority'

/**
 * Banner Slider CVA Configuration
 *
 * Design tokens from tailwind.config.js:
 * - main-color (#F7B01A) - Primary brand color (alias for main-color)
 * - custom-red (#d7263d) - Accent/destructive color
 * - cards-bg (#F5F5F5) - Background color
 * - border-color (#e5e5e5) - Border color
 * - text-color (#1c1b21) - Main text color
 */

/**
 * Banner section container
 */
export const bannerSectionVariants = cva('homepage-banner-slider mb-8')

export type BannerSectionVariants = VariantProps<typeof bannerSectionVariants>

/**
 * Banner item container (each slide)
 */
export const bannerItemVariants = cva(
  'relative overflow-hidden rounded bg-cards-bg'
)

export type BannerItemVariants = VariantProps<typeof bannerItemVariants>

/**
 * Banner link wrapper
 * Includes focus-visible for accessibility and visited state consistency
 */
export const bannerLinkVariants = cva(
  'block focus:outline-none focus-visible:ring-2 focus-visible:ring-main-color focus-visible:ring-offset-2 ' +
    'visited:text-inherit visited:no-underline'
)

export type BannerLinkVariants = VariantProps<typeof bannerLinkVariants>

/**
 * Banner image styling
 * Desktop: fixed ~2.74:1 aspect with object-contain (recommended 1920x700).
 * Mobile: width fills container, height natural (no forced aspect → no letterbox,
 * no cropping; whatever the image's real ratio is, it fits the full width).
 * Recommended upload mobile: 800x700 (~8:7).
 */
export const bannerImageVariants = cva('w-full', {
  variants: {
    visibility: {
      desktop: 'hidden md:block h-full aspect-[1920/700] object-contain',
      mobile: 'block md:hidden h-auto',
    },
  },
  defaultVariants: {
    visibility: 'desktop',
  },
})

export type BannerImageVariants = VariantProps<typeof bannerImageVariants>

/**
 * Banner fallback (when no images)
 * Matches loaded-banner aspect ratio so layout stays stable across states.
 */
export const bannerFallbackVariants = cva(
  'aspect-[8/7] md:aspect-[1920/700] bg-gradient-to-r from-main-color to-custom-red flex items-center justify-center'
)

export type BannerFallbackVariants = VariantProps<typeof bannerFallbackVariants>

/**
 * Banner fallback content container
 */
export const bannerFallbackContentVariants = cva('text-center text-white p-8')

export type BannerFallbackContentVariants = VariantProps<typeof bannerFallbackContentVariants>

/**
 * Banner fallback title
 */
export const bannerFallbackTitleVariants = cva('text-2xl md:text-4xl font-bold mb-4 font-main-font')

export type BannerFallbackTitleVariants = VariantProps<typeof bannerFallbackTitleVariants>

/**
 * Banner fallback CTA button
 */
export const bannerFallbackCtaVariants = cva(
  'inline-block px-6 py-2 bg-white text-main-color rounded-full font-medium font-main-font ' +
    'hover:bg-cards-bg transition-colors'
)

export type BannerFallbackCtaVariants = VariantProps<typeof bannerFallbackCtaVariants>

/**
 * Navigation button (prev/next arrows)
 *
 * Variants:
 * - size: 'banner' (larger, default) or 'product' (smaller)
 * - direction: 'prev' or 'next' (controls left/right position)
 */
export const bannerNavButtonVariants = cva(
  'absolute top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white ' +
    'rounded-full flex items-center justify-center transition-all duration-200 ' +
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ' +
    'disabled:opacity-50 disabled:cursor-not-allowed',
  {
    variants: {
      direction: {
        prev: '',
        next: '',
      },
      size: {
        // Banner slider: larger buttons
        banner: 'w-14 h-14 md:w-16 md:h-16 focus-visible:ring-main-color',
        // Product slider: smaller buttons with border
        product: 'w-10 h-10 md:w-12 md:h-12 border border-border-color ' +
          'backdrop-blur-sm focus-visible:ring-main-color',
      },
    },
    compoundVariants: [
      // Banner positioning
      { size: 'banner', direction: 'prev', class: 'left-1 md:left-4' },
      { size: 'banner', direction: 'next', class: 'right-1 md:right-4' },
      // Product positioning (slightly more inset)
      { size: 'product', direction: 'prev', class: 'left-2 md:left-4' },
      { size: 'product', direction: 'next', class: 'right-2 md:right-4' },
    ],
    defaultVariants: {
      direction: 'prev',
      size: 'banner',
    },
  }
)

export type BannerNavButtonVariants = VariantProps<typeof bannerNavButtonVariants>

/**
 * Navigation button icon
 * Size: w-7 h-7 = 28px on mobile, w-8 h-8 = 32px on desktop
 */
export const bannerNavIconVariants = cva('w-8 h-8 md:w-10 md:h-10 text-text-color')

export type BannerNavIconVariants = VariantProps<typeof bannerNavIconVariants>

/**
 * Swiper container styles
 */
export const bannerSwiperVariants = cva('relative')

export type BannerSwiperVariants = VariantProps<typeof bannerSwiperVariants>

/**
 * Banner skeleton (loading state)
 * Matches loaded-banner aspect ratio so layout stays stable across states.
 */
export const bannerSkeletonVariants = cva(
  'aspect-[8/7] md:aspect-[1920/700] rounded animate-pulse bg-muted'
)

export type BannerSkeletonVariants = VariantProps<typeof bannerSkeletonVariants>

/**
 * Swiper pagination CSS classes
 * These are used in Swiper config, not as CVA variants
 */
export const SWIPER_PAGINATION_BULLET =
  'swiper-pagination-bullet !w-3 !h-3 !bg-custom-gray !opacity-100'
export const SWIPER_PAGINATION_BULLET_ACTIVE = '!bg-main-color'
